import React from 'react';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Badge from '@mui/material/Badge';
import { useTheme } from '@mui/material/styles';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import useMediaQuery from '@mui/material/useMediaQuery';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useTranslate } from 'src/locales';
import { useSelector } from 'src/redux/store';
import { selectBetSlipBets } from 'src/redux/slices/betslip';

import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';

import Main from './main';
import Header from './header';
import Footer from './footer';
import NavMini from './nav-mini';
import NavSport from './nav-sport';
import NavVertical from './nav-vertical';
import BetslipModal from './bet-slip-modal';
import NavHorizontal from './nav-horizontal';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function DashboardLayout({ children }: Props) {
  const settings = useSettingsContext();

  const { t } = useTranslate();

  const theme = useTheme();

  const lgUp = useResponsive('up', 'lg');
  const isLandscape = useMediaQuery('(orientation: landscape)');

  const nav = useBoolean();
  const active_betslip = useBoolean();
  const active_choose_sport = useBoolean();

  const isHorizontal = settings.themeLayout === 'horizontal';

  const isMini = settings.themeLayout === 'mini';

  const renderNavMini = <NavMini />;
  const renderNavSport = <NavSport />;

  const renderHorizontal = <NavHorizontal />;

  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} />;
  const BetSlipModal = (
    <BetslipModal open={active_betslip.value} handleClose={active_betslip.onFalse} />
  );

  const betSlipBets = useSelector(selectBetSlipBets);

  const pathname = usePathname();

  const rafPage = pathname === '/raf';

  const renderBottomNav = (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: { xs: 'flex', lg: 'none' },
        justifyContent: 'center',
        alignItems: 'center',
        width: 1,
      }}
    >
      <BottomNavigation
        showLabels
        sx={{
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          height: '70px',
          width: 1,
          background: `linear-gradient(to right, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
          display: 'flex',
          justifyContent: 'space-around',
        }}
      >
        <BottomNavigationAction
          label={t('layout.home')}
          component={Link}
          to="/"
          icon={<Iconify icon="fluent:games-24-filled" />}
          sx={{ color: '#fff', minWidth: '64px' }}
        />

        <BottomNavigationAction
          label={t('layout.my_bets')}
          component={Link}
          to={paths.dashboard.user.account_menu('ticket')}
          icon={<Iconify icon="ph:ticket-duotone" />}
          sx={{ color: '#fff', minWidth: '64px' }}
        />

        <BottomNavigationAction
          label={t('layout.my_account')}
          component={Link}
          to={paths.dashboard.user.account_menu('transaction')}
          icon={<Iconify icon="fluent-emoji-high-contrast:money-bag" />}
          sx={{ color: '#fff', minWidth: '64px' }}
        />
      </BottomNavigation>
    </Box>
  );

  if (isHorizontal) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />
        <Box
          sx={{
            minHeight: 1,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          {/* {lgUp && renderNavSport} */}
          {!lgUp && renderNavVertical}
          {!lgUp && BetSlipModal}

          <Main>{children}</Main>
        </Box>
        <Footer />
        {!isLandscape && !rafPage && renderBottomNav}
      </>
    );
  }

  if (isMini) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        <Box
          sx={{
            minHeight: 1,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          {lgUp ? renderNavMini : renderNavVertical}
          {!lgUp && BetSlipModal}

          <Main>{children}</Main>
        </Box>
        <Footer />
        {!isLandscape && !rafPage && renderBottomNav}
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={nav.onTrue} />

      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        {renderNavVertical}
        {BetSlipModal}

        <Main>{children}</Main>
      </Box>
      <Footer />
      {!isLandscape && !rafPage && renderBottomNav}
    </>
  );
}
